import React, { useEffect, useMemo, useState } from 'react';
import {
  Callout, RedNote, Button, TextButton, Field, Input, Select, TYPE, cn,
} from '../ui';
import {
  CreditCard,
  Expense,
  Income,
  Loan,
  PayoffPlan,
  PlanMode,
} from '../../types';
import {
  ExtraPaymentSpec,
  ScheduleResult,
  generateLumpSum,
  solveForDate,
  solveForPayment,
} from '../../utils/finance/planMath';
import { buildLedger, thresholdBreaches } from '../../utils/finance/ledger';
import { projectCardBalance } from '../../utils/finance/cardLedger';
import { minimumPaymentForCard } from '../../utils/finance/minimums';
import {
  addMonths,
  parseLocalDate,
  toDateInputValue,
  toDayKey,
  toLocalDay,
  today as todayFn,
} from '../../utils/finance/dates';
import { PlanScheduleTable } from './PlanScheduleTable';
import { draftCardEvents, planSummaryLine, toPlanPayload, usd } from './planPayload';

interface Variation {
  id: string;
  label: string;
  mode: PlanMode;
  targetPayment: number;
  targetDate: string;
  lumpAmount: number;
  lumpDate: string;
  extras: ExtraPaymentSpec[];
  overrides: Record<string, number>;
  /** 0 = exact. Otherwise payments round UP to this step — down could leave a
   *  payment under the issuer minimum. */
  roundTo: number;
}

interface PayoffPlanBuilderProps {
  card: CreditCard;
  /** Every ACTIVE plan on this card — monthly and one-time. */
  existingPlans: PayoffPlan[];
  currentBalance: number;
  balanceAsOf: Date | null;
  lowBalanceThreshold: number;
  incomes: Income[];
  expenses: Expense[];
  loans: Loan[];
  creditCards: CreditCard[];
  plans: PayoffPlan[];
  /** `planId` present => update that stored plan; absent => create a new one. */
  onActivate: (payload: any, planId?: string) => Promise<void>;
  onRemovePlan: (planId: string) => Promise<void>;
  onClose: () => void;
}

const MAX_VARIATIONS = 3;

function makeVariation(card: CreditCard, index: number, balance: number): Variation {
  const min = minimumPaymentForCard(card, balance);
  return {
    id: `v${index}-${Date.now()}`,
    label: ['Plan A', 'Plan B', 'Plan C'][index] ?? `Plan ${index + 1}`,
    mode: 'TARGET_PAYMENT',
    targetPayment: Math.ceil(Math.max(min, balance / 24)),
    targetDate: toDateInputValue(addMonths(todayFn(), 24)),
    lumpAmount: Math.round(balance),
    lumpDate: toDateInputValue(todayFn()),
    extras: [],
    overrides: {},
    roundTo: 0,
  };
}

/**
 * Reconstruct editable builder state from a stored plan.
 *
 * Extras are stored as individual EXTRA rows, not as the recurring/one-off
 * specs the builder edits, so they're grouped back: the same (day-of-month,
 * amount) appearing more than once is a recurring extra; a lone occurrence is
 * a one-off.
 */
function planToVariation(plan: PayoffPlan): Variation {
  const extraRows = plan.payments.filter(p => p.kind === 'EXTRA');

  const groups = new Map<string, { dayOfMonth: number; amount: number; dates: Date[] }>();
  for (const r of extraRows) {
    const d = toLocalDay(r.dueDate);
    const key = `${d.getDate()}|${r.amount}`;
    const g = groups.get(key);
    if (g) g.dates.push(d);
    else groups.set(key, { dayOfMonth: d.getDate(), amount: r.amount, dates: [d] });
  }
  const extras: ExtraPaymentSpec[] = [];
  groups.forEach(g => {
    extras.push(
      g.dates.length > 1
        ? { type: 'recurring', dayOfMonth: g.dayOfMonth, amount: g.amount }
        : { type: 'oneOff', date: g.dates[0], amount: g.amount }
    );
  });

  const overrides: Record<string, number> = {};
  for (const p of plan.payments) {
    if (p.isCustomized && p.kind !== 'EXTRA') {
      overrides[toDayKey(toLocalDay(p.dueDate))] = p.amount;
    }
  }

  const firstScheduled = plan.payments.find(p => p.kind === 'SCHEDULED');
  const firstExtra = extraRows[0];

  return {
    id: `edit-${plan.id}`,
    label: plan.name || (plan.mode === 'LUMP_SUM' ? 'One-time payment' : 'Payoff plan'),
    mode: plan.mode,
    targetPayment: plan.targetPayment ?? firstScheduled?.amount ?? 0,
    targetDate: toDateInputValue(toLocalDay(plan.targetDate ?? plan.endDate)),
    lumpAmount: firstExtra?.amount ?? 0,
    lumpDate: toDateInputValue(toLocalDay(firstExtra?.dueDate ?? plan.startDate)),
    // A LUMP_SUM plan's payments are its extras; don't double-count them.
    extras: plan.mode === 'LUMP_SUM' ? [] : extras,
    overrides,
    roundTo: 0,
  };
}

export const PayoffPlanBuilder: React.FC<PayoffPlanBuilderProps> = ({
  card,
  existingPlans,
  currentBalance,
  balanceAsOf,
  lowBalanceThreshold,
  incomes,
  expenses,
  loans,
  creditCards,
  plans,
  onActivate,
  onRemovePlan,
  onClose,
}) => {
  // Opening a card that already has a plan loads that plan for editing rather
  // than showing a blank form.
  const initialPlan =
    existingPlans.find(pl => pl.mode !== 'LUMP_SUM') ?? existingPlans[0] ?? null;

  const [editingPlanId, setEditingPlanId] = useState<string | null>(initialPlan?.id ?? null);
  const [variations, setVariations] = useState<Variation[]>(() =>
    initialPlan
      ? [planToVariation(initialPlan)]
      // Seeded from the card's real balance. It used to be seeded with 0,
      // which made the opening payment 0 — below the card's own minimum — so a
      // new plan greeted you with "$0/mo · never clears the balance".
      : [makeVariation(card, 0, projectCardBalance(card, plans))]
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDays, setShowDays] = useState(false);
  const [fixing, setFixing] = useState(false);
  /** What the fixer concluded. Not an error — nothing failed. */
  const [fixNote, setFixNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Keep a selection at all times. The inputs live in one rail that edits the
   *  selected variation, so "nothing selected" means a builder with no
   *  controls at all — which is exactly how a new plan first opened. */
  useEffect(() => {
    if (!selectedId && variations.length > 0) setSelectedId(variations[0].id);
  }, [selectedId, variations]);

  // When re-solving an EXISTING plan, the starting balance must exclude that
  // plan's own payments — otherwise we'd be planning against a balance that
  // already assumes the plan ran, and every edit would compound.
  const startingBalance = useMemo(() => {
    const others = editingPlanId ? plans.filter(pl => pl.id !== editingPlanId) : plans;
    return projectCardBalance(card, others);
  }, [card, plans, editingPlanId]);

  const originAsOf = todayFn();
  const minPayment = minimumPaymentForCard(card, startingBalance);

  const loadPlanForEdit = (plan: PayoffPlan) => {
    setEditingPlanId(plan.id);
    setVariations([planToVariation(plan)]);
    setSelectedId(`edit-${plan.id}`);
    setError(null);
  };

  const startNewPlan = () => {
    setEditingPlanId(null);
    const fresh = makeVariation(card, 0, projectCardBalance(card, plans));
    setVariations([fresh]);
    setSelectedId(fresh.id);
    setError(null);
  };

  const removePlan = async (planId: string) => {
    if (!window.confirm('Remove this plan? The card goes back to minimum payments for the months it covered.')) return;
    await onRemovePlan(planId);
    if (editingPlanId === planId) startNewPlan();
  };

  const scheduleInput = {
    startingBalance,
    apr: card.interestRate,
    dueDay: card.dueDay ?? 1,
    minFloor: card.minimumPayment,
    principalPct: card.minPaymentPrincipalPercent ?? 1,
    originAsOf,
  };

  /** Round UP to the step. Down could put a payment under the minimum. */
  const roundUp = (n: number, step: number) => (step > 0 ? Math.ceil(n / step) * step : n);

  /** Solve each variation. */
  const solved = useMemo(() => {
    return variations.map(v => {
      let result: ScheduleResult;
      let warning: string | undefined;

      if (v.mode === 'LUMP_SUM') {
        result = generateLumpSum({
          startingBalance,
          apr: card.interestRate,
          amount: v.lumpAmount,
          date: parseLocalDate(v.lumpDate),
        });
      } else if (v.mode === 'TARGET_DATE') {
        const r = solveForPayment(
          { ...scheduleInput, extras: v.extras, overrides: v.overrides },
          parseLocalDate(v.targetDate)
        );
        if (v.roundTo > 0) {
          // Re-solve at the rounded figure so the schedule and the payoff date
          // agree with the number actually shown.
          const rounded = solveForDate(
            { ...scheduleInput, extras: v.extras, overrides: v.overrides },
            roundUp(r.result.monthlyPayment, v.roundTo)
          );
          result = rounded.result;
          warning = r.warning ?? rounded.warning;
        } else {
          result = r.result;
          warning = r.warning;
        }
      } else {
        const r = solveForDate(
          { ...scheduleInput, extras: v.extras, overrides: v.overrides },
          roundUp(v.targetPayment, v.roundTo)
        );
        result = r.result;
        warning = r.warning;
      }
      return { variation: v, result, warning };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variations, startingBalance, card.interestRate, card.dueDay, card.minimumPayment]);

  /**
   * The "everything except this card" cash ledger, computed ONCE. Each
   * candidate schedule is composed onto it rather than rebuilding the whole
   * projection per variation per keystroke.
   */
  const horizonEnd = useMemo(() => addMonths(todayFn(), 24), []);
  const baseLedgerInput = useMemo(
    () => ({
      openingBalance: currentBalance,
      openingDate: balanceAsOf ?? todayFn(),
      from: todayFn(),
      to: horizonEnd,
      incomes,
      expenses,
      loans,
      creditCards,
      plans,
      lowBalanceThreshold,
      excludeCardIds: [card.id],
    }),
    [currentBalance, balanceAsOf, horizonEnd, incomes, expenses, loans, creditCards, plans, lowBalanceThreshold, card.id]
  );

  type Breach = { date: Date; balance: number; isNegative: boolean };
  type CashImpact = {
    breaches: Breach[];
    /** 'YYYY-MM' → the lowest projected checking balance in that month. The
     *  schedule shows this beside each payment, so the cost of a month is
     *  legible next to the month that causes it. */
    floorByMonth: Map<string, number>;
  };

  /** Project the checking account with a candidate schedule spliced in. */
  const cashImpact = (result: ScheduleResult): CashImpact => {
    const days = buildLedger({
      ...baseLedgerInput,
      extraCardEvents: draftCardEvents(card, result),
    });
    const floorByMonth = new Map<string, number>();
    for (const d of days) {
      if (d.isHistorical) continue;
      const key = `${d.date.getFullYear()}-${String(d.date.getMonth() + 1).padStart(2, '0')}`;
      const seen = floorByMonth.get(key);
      if (seen === undefined || d.balance < seen) floorByMonth.set(key, d.balance);
    }
    return { breaches: thresholdBreaches(days), floorByMonth };
  };

  const impactByVariation = useMemo(() => {
    const map = new Map<string, CashImpact>();
    for (const s of solved) map.set(s.variation.id, cashImpact(s.result));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solved, baseLedgerInput, card]);

  /** Spare cash per month with everything except this card's payments — the
   *  ceiling on what can go to it. Measured as the net change across the
   *  horizon rather than income minus outgoings, so one-offs land in the
   *  average where they belong. */
  const freeCashFlow = useMemo(() => {
    const days = buildLedger(baseLedgerInput);
    const real = days.filter(d => !d.isHistorical);
    if (real.length < 2) return null;
    const months = real.length / 30.44;
    if (months < 1) return null;
    return (real[real.length - 1].balance - real[0].balance) / months;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseLedgerInput]);

  const breachesOf = (id: string): Breach[] => impactByVariation.get(id)?.breaches ?? [];

  const selected = solved.find(s => s.variation.id === selectedId) ?? null;

  const patch = (id: string, changes: Partial<Variation>) =>
    setVariations(vs => vs.map(v => (v.id === id ? { ...v, ...changes } : v)));


  /**
   * The largest monthly payment that never drops the checking balance below
   * the floor. Payment and breach count move together — more to the card is
   * less in the account — so this bisects rather than stepping, which keeps it
   * to ~14 ledger builds instead of hundreds.
   *
   * Returns null when even the issuer minimum breaches: the floor is not
   * reachable by paying this card less, and saying so is more use than
   * silently landing on the minimum.
   */
  const solveWithinFloor = (v: Variation): number | null => {
    const breachesAt = (payment: number): number => {
      const r = solveForDate(
        { ...scheduleInput, extras: v.extras, overrides: {} },
        payment
      );
      return cashImpact(r.result).breaches.length;
    };
    if (breachesAt(minPayment) > 0) return null;

    let lo = minPayment;
    let hi = Math.max(minPayment, v.targetPayment);
    if (breachesAt(hi) === 0) return hi;
    for (let i = 0; i < 14 && hi - lo > 1; i++) {
      const mid = Math.round((lo + hi) / 2);
      if (breachesAt(mid) === 0) lo = mid;
      else hi = mid;
    }
    return lo;
  };

  /** Solve a variation with a given override set — used to ask what a change
   *  would do before committing it. */
  const solveWith = (v: Variation, overrides: Record<string, number>): ScheduleResult => {
    if (v.mode === 'LUMP_SUM') {
      return generateLumpSum({
        startingBalance,
        apr: card.interestRate,
        amount: v.lumpAmount,
        date: parseLocalDate(v.lumpDate),
      });
    }
    const input = { ...scheduleInput, extras: v.extras, overrides };
    return v.mode === 'TARGET_DATE'
      ? solveForPayment(input, parseLocalDate(v.targetDate)).result
      : solveForDate(input, roundUp(v.targetPayment, v.roundTo)).result;
  };

  /**
   * Cap the payment in every month whose floor breaches, down to the issuer
   * minimum. Written as per-month overrides rather than a mode, so the
   * schedule shows precisely which months changed and each one can be undone
   * on its own row.
   *
   * An action rather than the artboard's toggle: a toggle promises the floor
   * stays protected as you keep editing, and holding that would mean
   * re-solving the whole projection on every keystroke. This does it once,
   * says how many months it touched, and leaves them visible.
   */
  const handleProtectFloor = () => {
    if (!selected || lowBalanceThreshold <= 0) return;
    setFixing(true);
    try {
      const impact = cashImpact(selected.result);
      const next = { ...selected.variation.overrides };
      let touched = 0;
      for (const p of selected.result.payments) {
        if (p.kind === 'EXTRA') continue;
        const key = `${p.dueDate.getFullYear()}-${String(p.dueDate.getMonth() + 1).padStart(2, '0')}`;
        const floor = impact.floorByMonth.get(key);
        if (floor === undefined || floor >= lowBalanceThreshold) continue;
        const min = minimumPaymentForCard(card, p.openingBalance);
        if (p.amount <= min + 0.005) continue;
        next[toDayKey(p.dueDate)] = Math.round(min * 100) / 100;
        touched++;
      }
      if (touched === 0) {
        setFixNote(
          `No month can give any more back — every breaching month is already at this card's minimum.`
        );
        return;
      }

      // Say what it achieved, not just what it did. Capping trades payoff date
      // and interest for headroom, and both numbers move a long way.
      const before = impact.breaches.length;
      const afterResult = solveWith(selected.variation, next);
      const after = cashImpact(afterResult).breaches.length;
      const payoff = afterResult.payoffDate
        ? afterResult.payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
        : 'never';
      const costlier = afterResult.totalInterest - selected.result.totalInterest;

      setFixNote(
        `Capped ${touched} ${touched === 1 ? 'month' : 'months'} at the minimum. ` +
          `Days below the floor: ${before} → ${after}` +
          (after > 0 ? ' — the rest is shortfall this card cannot reach.' : '.') +
          ` Payoff moves to ${payoff}` +
          (costlier > 1 ? `, costing ${usd(costlier)} more interest.` : '.')
      );
      patch(selected.variation.id, { overrides: next });
    } finally {
      setFixing(false);
    }
  };

  const handleFix = () => {
    if (!selected) return;
    setFixing(true);
    try {
      const safe = solveWithinFloor(selected.variation);
      if (safe === null) {
        setFixNote(
          `Even this card's minimum of ${usd(minPayment)} leaves you below ${usd(lowBalanceThreshold)}, ` +
            'so paying it less cannot lift you over the floor — the shortfall is elsewhere in those months.'
        );
        return;
      }
      setFixNote(
        `Set to ${usd(safe)} a month, the most that stays above ${usd(lowBalanceThreshold)} the whole way.`
      );
      patch(selected.variation.id, {
        mode: 'TARGET_PAYMENT',
        targetPayment: safe,
        overrides: {},
      });
    } finally {
      setFixing(false);
    }
  };

  const handleActivate = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const v = selected.variation;
      await onActivate(
        toPlanPayload({
          card,
          mode: v.mode,
          result: selected.result,
          name: v.label,
          targetDate: v.mode === 'TARGET_DATE' ? parseLocalDate(v.targetDate) : null,
          targetPayment: v.mode === 'TARGET_PAYMENT' ? v.targetPayment : null,
          originBalance: startingBalance,
          originAsOf,
        }),
        editingPlanId ?? undefined
      );
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to activate the plan.');
    } finally {
      setSaving(false);
    }
  };

  const MODES: [PlanMode, string][] = [
    ['TARGET_PAYMENT', 'Monthly'],
    ['TARGET_DATE', 'Target date'],
    ['LUMP_SUM', 'Lump sum'],
  ];

  /** What this variation does to your cash, as one of three states. Silence is
   *  not one of them: "no warning" and "not computed" look identical, which is
   *  the ambiguity the safe chip exists to remove. */
  const riskOf = (
    result: ScheduleResult,
    breaches: ReturnType<typeof thresholdBreaches>
  ): { tone: 'safe' | 'warn' | 'bad'; text: string } => {
    if (!result.isFeasible) return { tone: 'bad', text: '! Never clears the balance' };
    const negative = breaches.filter(b => b.isNegative);
    if (negative.length > 0) {
      return { tone: 'bad', text: `! Negative on ${negative.length} ${negative.length === 1 ? 'day' : 'days'}` };
    }
    if (breaches.length > 0) {
      return { tone: 'warn', text: `▲ Below ${usd(lowBalanceThreshold)} on ${breaches.length} ${breaches.length === 1 ? 'day' : 'days'}` };
    }
    if (lowBalanceThreshold > 0) {
      return { tone: 'safe', text: `✓ Never below ${usd(lowBalanceThreshold)}` };
    }
    return { tone: 'safe', text: '✓ Clears the balance' };
  };

  const RISK_TONE = {
    safe: 'text-money-in bg-money-in-tint',
    warn: 'text-warn bg-warn-tint',
    bad: 'text-bad bg-bad-tint',
  } as const;

  // ---------------------------------------------------------------- render

  return (
    <>
      {/* The builder owns the screen, so it carries its own bar rather than a
          page header plus a footer at the other end of a long scroll. */}
      <div className="sticky top-[var(--app-header-h)] z-20 -mx-5 sm:-mx-[44px] px-5 sm:px-[44px] bg-surface border-b border-rule-base">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-3">
          <div className="flex items-center gap-4 min-w-0">
            <TextButton onClick={onClose}>{'\u2039'} Close</TextButton>
            <span className="w-px h-5 bg-rule-base hidden sm:block" aria-hidden="true" />
            <span className={cn(TYPE.formTitle, 'text-ink truncate')}>
              Payoff plan — {card.name}
            </span>
            <span className={cn(TYPE.meta, 'text-ink-muted whitespace-nowrap hidden md:inline')}>
              {usd(startingBalance)} · {card.interestRate}% APR ·{' '}
              {card.dueDay ? `due day ${card.dueDay}` : 'no due day'} · min {usd(minPayment)}
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button variant="secondary" onClick={onClose}>Discard</Button>
            <Button
              variant="primary"
              onClick={handleActivate}
              disabled={saving || !selected || selected.result.payments.length === 0}
            >
              {saving
                ? 'Saving…'
                : editingPlanId
                  ? 'Save changes'
                  : `Save ${selected?.variation.label ?? 'plan'}`}
            </Button>
          </div>
        </div>
      </div>

      {!card.dueDay && (
        <RedNote className="mt-7">
          <p className={cn(TYPE.small, 'text-ink-soft')}>
            This card has no payment due day set, so payments are scheduled on the 1st.
            Set a due day on the card for accurate dates.
          </p>
        </RedNote>
      )}

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-[340px_minmax(0,1fr)] gap-7 items-start">
        {/* ── Control rail ────────────────────────────────────────────────
            One set of inputs, editing whichever variation is selected. They
            used to be duplicated inside every comparison card, so three
            variations meant three copies of the form down the page. */}
        <div className="flex flex-col gap-6">
          {existingPlans.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>Plans on this card</div>
              <div className="bg-surface border border-rule-base">
                {existingPlans.map(pl => {
                  const isEditing = editingPlanId === pl.id;
                  return (
                    <div
                      key={pl.id}
                      className={cn(
                        'px-4 py-3 border-b border-rule last:border-b-0',
                        isEditing && 'bg-accent-tint'
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className={cn(TYPE.rowTitle, 'text-ink truncate')}>
                          {pl.name || (pl.mode === 'LUMP_SUM' ? 'One-time payment' : 'Payoff plan')}
                        </span>
                        {isEditing ? (
                          <span className={cn(TYPE.tag, 'text-accent shrink-0')}>Editing</span>
                        ) : (
                          <TextButton onClick={() => loadPlanForEdit(pl)}>Edit</TextButton>
                        )}
                      </div>
                      <div className={cn(TYPE.meta, 'text-ink-muted mt-1 truncate')}>
                        {planSummaryLine(pl)}
                      </div>
                      <div className="mt-2">
                        <TextButton onClick={() => removePlan(pl.id)}>Remove</TextButton>
                      </div>
                    </div>
                  );
                })}
              </div>
              <TextButton onClick={startNewPlan}>Build a different plan instead</TextButton>
            </div>
          )}

          {selected && (
            <>
              <div className="flex flex-col gap-2.5">
                <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>Plan by</div>
                {/* One control, three states — three separate chips read as
                    three independent toggles. */}
                <div
                  role="group"
                  aria-label="Plan by"
                  className="grid grid-cols-3 border border-rule-strong rounded bg-surface overflow-hidden"
                >
                  {MODES.map(([mode, label], i) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={selected.variation.mode === mode}
                      onClick={() => patch(selected.variation.id, { mode, overrides: {} })}
                      className={cn(
                        TYPE.chip,
                        'py-2.5 px-1 text-center transition-colors duration-[120ms]',
                        i > 0 && 'border-l border-rule-base',
                        selected.variation.mode === mode
                          ? 'bg-ink text-surface'
                          : 'text-ink-soft hover:text-ink'
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-surface border border-rule-base p-5 flex flex-col gap-5">
                <Field label="Plan name" htmlFor="plan-name">
                  <Input
                    id="plan-name"
                    type="text"
                    value={selected.variation.label}
                    onChange={e => patch(selected.variation.id, { label: e.target.value })}
                  />
                </Field>

                {selected.variation.mode === 'TARGET_PAYMENT' && (
                  <Field
                    label="Monthly payment"
                    hint={
                      freeCashFlow !== null && freeCashFlow > 0
                        ? `Minimum ${usd(minPayment)} · about ${usd(freeCashFlow)}/mo spare`
                        : `Minimum ${usd(minPayment)}`
                    }
                    htmlFor="plan-payment"
                  >
                    <Input
                      id="plan-payment"
                      type="number"
                      min={0}
                      step="1"
                      value={selected.variation.targetPayment}
                      onChange={e =>
                        patch(selected.variation.id, { targetPayment: parseFloat(e.target.value) || 0 })
                      }
                    />
                  </Field>
                )}

                {selected.variation.mode === 'TARGET_DATE' && (
                  <Field label="Pay off by" htmlFor="plan-date">
                    <Input
                      id="plan-date"
                      type="date"
                      value={selected.variation.targetDate}
                      onChange={e => patch(selected.variation.id, { targetDate: e.target.value })}
                    />
                  </Field>
                )}

                {selected.variation.mode === 'LUMP_SUM' && (
                  <>
                    <Field label="Amount" htmlFor="plan-lump">
                      <Input
                        id="plan-lump"
                        type="number"
                        min={0}
                        step="1"
                        value={selected.variation.lumpAmount}
                        onChange={e =>
                          patch(selected.variation.id, { lumpAmount: parseFloat(e.target.value) || 0 })
                        }
                      />
                    </Field>
                    <Field
                      label="Payment date"
                      hint="The card keeps its minimum schedule; this stacks on top."
                      htmlFor="plan-lumpdate"
                    >
                      <Input
                        id="plan-lumpdate"
                        type="date"
                        value={selected.variation.lumpDate}
                        onChange={e => patch(selected.variation.id, { lumpDate: e.target.value })}
                      />
                    </Field>
                  </>
                )}

                {selected.variation.mode !== 'LUMP_SUM' && (
                  <Field label="Round payments to" htmlFor="plan-round">
                    <Select
                      id="plan-round"
                      value={String(selected.variation.roundTo)}
                      onChange={e =>
                        patch(selected.variation.id, { roundTo: parseInt(e.target.value, 10) || 0 })
                      }
                    >
                      <option value="0">Exact amount</option>
                      <option value="5">Nearest $5</option>
                      <option value="25">Nearest $25</option>
                      <option value="50">Nearest $50</option>
                      <option value="100">Nearest $100</option>
                    </Select>
                  </Field>
                )}

                {selected.variation.mode !== 'LUMP_SUM' && (
                  <div className="pt-4 border-t border-rule flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className={cn(TYPE.sectionLabel, 'text-ink-muted')}>Monthly extras</span>
                      <TextButton
                        onClick={() =>
                          patch(selected.variation.id, {
                            extras: [
                              ...selected.variation.extras,
                              { type: 'recurring', dayOfMonth: 15, amount: 100 },
                            ],
                          })
                        }
                      >
                        Add
                      </TextButton>
                    </div>

                    {selected.variation.extras.map((extra, i) => (
                      <div key={i} className="flex flex-col gap-3">
                        <div className="flex items-end gap-3">
                        <Field label="Day" htmlFor={`extra-${i}-day`} className="w-[72px]">
                          <Input
                            id={`extra-${i}-day`}
                            type="number"
                            min={1}
                            max={31}
                            value={extra.type === 'recurring' ? extra.dayOfMonth : 1}
                            onChange={e => {
                              const next = [...selected.variation.extras];
                              next[i] = {
                                type: 'recurring',
                                dayOfMonth: parseInt(e.target.value) || 1,
                                amount: extra.amount,
                              };
                              patch(selected.variation.id, { extras: next });
                            }}
                          />
                        </Field>
                        <Field label="Amount" htmlFor={`extra-${i}-amount`} className="flex-1">
                          <Input
                            id={`extra-${i}-amount`}
                            type="number"
                            min={0}
                            step="1"
                            value={extra.amount}
                            onChange={e => {
                              const next = [...selected.variation.extras];
                              next[i] = { ...extra, amount: parseFloat(e.target.value) || 0 } as ExtraPaymentSpec;
                              patch(selected.variation.id, { extras: next });
                            }}
                          />
                        </Field>
                        <div className="pb-2">
                          <TextButton
                            aria-label={`Remove extra ${i + 1}`}
                            onClick={() =>
                              patch(selected.variation.id, {
                                extras: selected.variation.extras.filter((_, j) => j !== i),
                              })
                            }
                          >
                            ✕
                          </TextButton>
                        </div>
                        </div>
                        <Field
                          label="Applied from"
                          hint="Leave blank to start with the first payment."
                          htmlFor={`extra-${i}-from`}
                        >
                          <Input
                            id={`extra-${i}-from`}
                            type="date"
                            value={
                              extra.type === 'recurring' && extra.from
                                ? toDateInputValue(extra.from)
                                : ''
                            }
                            onChange={e => {
                              const next = [...selected.variation.extras];
                              next[i] = {
                                type: 'recurring',
                                dayOfMonth: extra.type === 'recurring' ? extra.dayOfMonth : 1,
                                amount: extra.amount,
                                from: e.target.value ? parseLocalDate(e.target.value) : undefined,
                              };
                              patch(selected.variation.id, { extras: next });
                            }}
                          />
                        </Field>
                      </div>
                    ))}

                    {lowBalanceThreshold > 0 && (
                      <div className="pt-3 border-t border-rule">
                        <TextButton onClick={handleProtectFloor} disabled={fixing}>
                          Protect the {usd(lowBalanceThreshold)} floor
                        </TextButton>
                        <p className={cn(TYPE.meta, 'text-ink-muted mt-1.5 leading-[1.5]')}>
                          Caps any month that dips below it at this card's minimum.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Comparison ──────────────────────────────────────────────── */}
        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-4">
            <span className={cn(TYPE.sectionLabel, 'text-ink-muted')}>Compare variations</span>
            {variations.length < MAX_VARIATIONS ? (
              <TextButton
                onClick={() =>
                  setVariations(vs => [...vs, makeVariation(card, vs.length, startingBalance)])
                }
              >
                + Add variation ({MAX_VARIATIONS} max)
              </TextButton>
            ) : (
              <span className={cn(TYPE.meta, 'text-ink-muted')}>{MAX_VARIATIONS} max</span>
            )}
          </div>

          <div
            className={cn(
              'grid gap-4',
              variations.length === 1
                ? 'grid-cols-1'
                : variations.length === 2
                  ? 'grid-cols-1 sm:grid-cols-2'
                  : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'
            )}
          >
            {solved.map(({ variation: v, result, warning }, i) => {
              const breaches = breachesOf(v.id);
              const isSelected = selectedId === v.id;
              const regular = result.payments.find(p => p.kind === 'SCHEDULED');
              const risk = riskOf(result, breaches);
              const headline =
                v.mode === 'LUMP_SUM'
                  ? usd(v.lumpAmount)
                  : regular
                    ? usd(regular.amount)
                    : '—';
              return (
                <div
                  key={v.id}
                  onClick={() => setSelectedId(v.id)}
                  className={cn(
                    'bg-surface border p-4 flex flex-col gap-3 cursor-pointer transition-colors duration-[120ms]',
                    isSelected
                      ? 'border-accent shadow-focus-accent'
                      : 'border-rule-base hover:border-rule-strong'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={cn(
                        TYPE.tag,
                        'w-[22px] h-[22px] leading-[22px] text-center rounded-xs',
                        isSelected ? 'bg-accent text-accent-on' : 'bg-ground-sunk text-ink-soft'
                      )}
                    >
                      {String.fromCharCode(65 + i)}
                    </span>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); setSelectedId(v.id); }}
                      className={cn(TYPE.tag, isSelected ? 'text-accent' : 'text-ink-muted hover:text-ink')}
                    >
                      {isSelected ? '✓ Selected' : 'Select'}
                    </button>
                  </div>

                  <div className={cn(TYPE.rowTitle, 'text-ink truncate')}>{v.label}</div>

                  <div className="flex items-baseline gap-2">
                    <span className={cn(TYPE.heroFigure, 'text-ink')}>{headline}</span>
                    <span className={cn(TYPE.meta, 'text-ink-muted')}>
                      {v.mode === 'LUMP_SUM' ? 'once' : '/mo'}
                    </span>
                  </div>

                  <dl className="flex flex-col gap-2 pt-1">
                    <SpecRow
                      label="Payoff"
                      value={
                        result.payoffDate
                          ? result.payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                          : 'Never'
                      }
                      bad={!result.payoffDate}
                    />
                    <SpecRow label="Interest paid" value={usd(result.totalInterest)} />
                    <SpecRow label="Payments" value={String(result.payments.length)} />
                  </dl>

                  <div className={cn(TYPE.tag, 'px-2 py-1.5 mt-auto', RISK_TONE[risk.tone])}>
                    {risk.text}
                  </div>

                  {warning && (
                    <p className={cn(TYPE.small, 'text-ink-soft')}>{warning}</p>
                  )}

                  {variations.length > 1 && (
                    <div className="self-start" onClick={e => e.stopPropagation()}>
                      <TextButton
                        onClick={() => {
                          setVariations(vs => vs.filter(x => x.id !== v.id));
                          if (selectedId === v.id) setSelectedId(null);
                        }}
                      >
                        Remove
                      </TextButton>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selected && (() => {
            const breaches = breachesOf(selected.variation.id);
            if (breaches.length === 0) return null;
            const negative = breaches.filter(b => b.isNegative);
            const bad = negative.length > 0;
            return (
              <div
                className={cn(
                  'border-l-2 px-4 py-3 flex flex-col gap-1',
                  bad ? 'border-bad bg-bad-tint' : 'border-warn bg-warn-tint'
                )}
              >
                <div className={cn(TYPE.rowTitle, 'text-ink')}>
                  {bad
                    ? `${'\u0021'} Goes negative on ${negative.length} ${negative.length === 1 ? 'day' : 'days'}`
                    : `${'\u25B2'} Drops below ${usd(lowBalanceThreshold)} on ${breaches.length} ${breaches.length === 1 ? 'day' : 'days'}`}
                </div>
                <p className={cn(TYPE.small, 'text-ink-soft')}>
                  First on {breaches[0].date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                  {breaches[0].balance !== undefined && `, at ${usd(breaches[0].balance)}`}.
                </p>

                <div className="flex flex-wrap items-center gap-4 mt-1">
                  <TextButton onClick={() => setShowDays(o => !o)}>
                    {showDays ? 'Hide days' : `Show ${breaches.length === 1 ? 'the day' : 'all days'}`}
                  </TextButton>
                  {selected.variation.mode !== 'LUMP_SUM' && (
                    <TextButton onClick={handleFix} disabled={fixing}>
                      {fixing ? 'Working…' : 'Fix for me'}
                    </TextButton>
                  )}
                </div>

                {showDays && (
                  <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                    {breaches.slice(0, 24).map((b, i) => (
                      <li
                        key={i}
                        className={cn(TYPE.meta, b.isNegative ? 'text-bad' : 'text-ink-soft')}
                      >
                        {b.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
                        {usd(b.balance)}
                      </li>
                    ))}
                    {breaches.length > 24 && (
                      <li className={cn(TYPE.meta, 'text-ink-muted')}>
                        + {breaches.length - 24} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })()}

          {!selected?.result.isFeasible && selected && (
            <RedNote>
              <p className={cn(TYPE.small, 'text-ink-soft')}>
                This plan never fully clears the balance. You can still save it — the schedule is
                capped at 10 years.
              </p>
            </RedNote>
          )}

          {fixNote && (
            <div className="border-l-2 border-accent px-4 py-3">
              <p className={cn(TYPE.small, 'text-ink-soft')}>{fixNote}</p>
            </div>
          )}

          {error && (
            <Callout>
              <div className={cn(TYPE.tag, 'text-bad')}>Could not save</div>
              <p className={cn(TYPE.small, 'text-ink-soft mt-2')}>{error}</p>
            </Callout>
          )}
        </div>
      </div>

      {/* ── Schedule, full width under both columns ───────────────────── */}
      {selected && (
        <section className="mt-9">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4">
            <span className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
              {selected.variation.label} — schedule
            </span>
            <span className={cn(TYPE.meta, 'text-ink-muted')}>
              {selected.result.payments.length} payments · click any amount to override
            </span>
          </div>
          <PlanScheduleTable
            payments={selected.result.payments}
            breachDayKeys={new Set(breachesOf(selected.variation.id).map(b => toDayKey(b.date)))}
            floorByMonth={impactByVariation.get(selected.variation.id)?.floorByMonth}
            threshold={lowBalanceThreshold}
            onOverride={(dayKey, amount) => {
              const next = { ...selected.variation.overrides };
              if (amount === null) delete next[dayKey];
              else next[dayKey] = amount;
              patch(selected.variation.id, { overrides: next });
            }}
          />
        </section>
      )}
    </>
  );
};

/** A label/value pair inside a variation column. */
const SpecRow: React.FC<{ label: string; value: string; bad?: boolean }> = ({
  label,
  value,
  bad,
}) => (
  <div className="flex justify-between gap-3">
    <dt className={cn(TYPE.metaTight, 'text-ink-muted')}>{label}</dt>
    <dd className={cn('text-[13.5px] font-semibold tabular-nums', bad ? 'text-bad' : 'text-ink')}>
      {value}
    </dd>
  </div>
);
